import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Card,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../utils/api";

const ArchitecturalRequestVisits = ({
  architectural_request_id,
  currentUserId,
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/getEvents`);
        const filteredEvents = (response.data || []).filter(
          (event) =>
            event.architectural_request_id === Number(architectural_request_id)
        );
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    if (architectural_request_id) {
      fetchEvents();
    }
  }, [architectural_request_id]);

  const fetchComments = async (eventId) => {
    setIsCommentsLoading(true);
    try {
      const response = await api.get(
        `/getArchitecturalComments/${architectural_request_id}/${eventId}`
      );
      setComments(response.data || []);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      setComments([]);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const handleOpenComments = (eventId) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
    setNewComment("");
    fetchComments(eventId);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      setSnackbar({
        open: true,
        message: "Comment cannot be empty.",
        severity: "warning",
      });
      return;
    }

    try {
      await api.post(`/addArchitecturalComment`, {
        architectural_request_id,
        event_id: selectedEventId,
        user_id: currentUserId,
        comment_text: newComment,
        is_internal: false,
      });

      setComments((prevComments) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
        },
        ...prevComments,
      ]);

      setNewComment("");
      setSnackbar({
        open: true,
        message: "Comment added successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to add comment:", error);
      setSnackbar({
        open: true,
        message: "Failed to add comment. Please try again.",
        severity: "error",
      });
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const modalPaperSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "92%", sm: 560 },
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: "rgba(255,255,255,0.98)",
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No visits found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
          There are no visits associated with this architectural request.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <EventAvailableIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Visits
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review scheduled visits and add comments for each event.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: "14px",
              border: "1px solid rgba(47,99,173,0.08)",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f7f9fc",
                    "& .MuiTableCell-root": {
                      fontWeight: 700,
                      color: "#223142",
                    },
                  }}
                >
                  <TableCell>Event ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {events.map((event, index) => (
                  <TableRow
                    key={event.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(47,99,173,0.03)",
                      },
                      "& .MuiTableCell-root": {
                        borderBottom:
                          index === events.length - 1
                            ? "none"
                            : "1px solid rgba(47,99,173,0.06)",
                        color: "#223142",
                      },
                    }}
                  >
                    <TableCell>{event.id}</TableCell>
                    <TableCell>{event.title || "N/A"}</TableCell>
                    <TableCell>{event.description || "N/A"}</TableCell>
                    <TableCell>
                      {event.start_time
                        ? new Date(event.start_time).toLocaleString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {event.end_time
                        ? new Date(event.end_time).toLocaleString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>{event.created_by_user_id || "N/A"}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenComments(event.id)}
                        sx={secondaryButtonSx}
                      >
                        View Comments
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Card>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="comments-modal"
      >
        <Box sx={modalPaperSx}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Box>
              <Typography
                id="comments-modal"
                variant="h6"
                sx={{ fontWeight: 700, color: "#223142" }}
              >
                Visit Comments
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.4 }}>
                Architectural Request ID: {architectural_request_id} • Event ID:{" "}
                {selectedEventId}
              </Typography>
            </Box>

            <IconButton onClick={() => setIsModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            {isCommentsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            ) : comments.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {comments.map((comment, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "12px",
                      backgroundColor: "rgba(47,99,173,0.02)",
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 0.75, color: "#223142" }}>
                      {comment.comment_text}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b778c" }}>
                      Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                      {new Date(comment.sent_at).toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                No comments available.
              </Typography>
            )}

            <TextField
              fullWidth
              label="Add a Comment"
              multiline
              rows={4}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ ...textFieldSx, mt: 2, mb: 2 }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleAddComment}
                sx={primaryButtonSx}
              >
                Save Comment
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchitecturalRequestVisits;
